import { Component } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MessageService } from 'primeng/api';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { FileProgressEvent, FileSelectEvent, FileUploadHandlerEvent, FileUploadModule } from 'primeng/fileupload';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import {
    ShipmentFileDownloadUrlCallback,
    GoogleCloudStorageService,
    ShipmentUploadProgress
} from 'src/app/services/google/google-cloud-storage.service';
import { CommonModule } from '@angular/common';
import { SelectModule } from 'primeng/select';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FieldsetModule } from "primeng/fieldset";
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { IftaLabelModule } from "primeng/iftalabel";
import { ButtonModule } from "primeng/button";
import { ProgressBarModule } from 'primeng/progressbar';
import { ShipmentDeliveryAttachment } from '../../shipment/models/shipment-delivery.attachment';

@Component({
    selector: 'app-shipment-file-upload',
    templateUrl: './shipment-file-upload.component.html',
    styleUrls: ['./shipment-file-upload.component.css'],
    standalone: true,
    providers: [MessageService],
    imports: [
    CommonModule,
    SelectModule,
    FormsModule,
    ReactiveFormsModule,
    FileUploadModule,
    FieldsetModule,
    InputGroupModule,
    InputGroupAddonModule,
    IftaLabelModule,
    ButtonModule,
    ProgressBarModule
]
})
export class ShipmentFileUploadComponent {

  readonly acceptedFileTypes = [
    'application/pdf',
    'image/*',
    'video/*',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'text/csv',
    'text/plain',
    '.pdf',
    '.jpg',
    '.jpeg',
    '.png',
    '.gif',
    '.webp',
    '.heic',
    '.heif',
    '.mp4',
    '.mov',
    '.avi',
    '.mkv',
    '.webm',
    '.doc',
    '.docx',
    '.xls',
    '.xlsx',
    '.ppt',
    '.pptx',
    '.csv',
    '.txt',
    '.rtf'
  ].join(',');
  readonly maxFileSize = 104857600;
  readonly supportedFileHint = 'Supported files: PDF, images, video, Word, Excel, PowerPoint, CSV, and text files. Max 100 MB per file.';
  filesSelected = false;
  uploadedFiles: UploadFileState[] = [];
  fileTypes = [
    'Bill of Lading',
    'Booking Confirmation',
    'Certificate of Origin',
    'Commercial Invoice',
    'Customer Purchase Order',
    'Customs Declaration',
    'Damage Report',
    'Delivery Note',
    'Dispatch Note',
    'Driver Documents',
    'Gate Pass',
    'Goods Receipt Note',
    'Historical Waybill',
    'Insurance Certificate',
    'Loading Evidence',
    'Packing List',
    'Proof of Delivery',
    'Rate Confirmation',
    'Sales Order',
    'Shipment Contract',
    'Transport Agreement',
    'Unloading Evidence',
    'Vehicle Documents',
    'Waybill',
    'Weight Ticket',
    'Miscellaneous'
  ];
  selectedFileType = '';
  lockFileType = false;
  allowMultiple = true;
  stopId?: number;
  uploadProgress: number = 0;
  uploadOngoing = false;
  uploadError = '';

  shipmentFileDownloadUrlCallback: ShipmentFileDownloadUrlCallback;



  constructor(private sessionStorageService: SessionStorageService,
    private messageService: MessageService, private config: DynamicDialogConfig,
    private googleCloudStorageService: GoogleCloudStorageService,
    private sanitizer: DomSanitizer) {
      this.shipmentFileDownloadUrlCallback = config.data.callback;
      this.selectedFileType = config.data.defaultFileType ?? '';
      this.lockFileType = !!config.data.lockFileType;
      this.allowMultiple = config.data.allowMultiple !== false;
      this.stopId = config.data.stopId;
    }

  ngOnInit(): void { }

  ngOnDestroy(): void {
    this.revokePreviewUrls();
  }

  onShipmentFileUpload(event: FileUploadHandlerEvent) {
    if (this.uploadOngoing) return;
    this.uploadError = '';
    if (!this.selectedFileType) {
      this.uploadError = 'Select a file type before uploading.';
      this.messageService.add({ severity: 'warn', summary: 'File type required', detail: this.uploadError });
      return;
    }

    const uploadContext = this.sessionStorageService.getShipmentUploadContext();
    if (!uploadContext?.shipmentId) {
      this.uploadError = 'Shipment context is missing. Reload the shipment and try again.';
      this.messageService.add({ severity: 'error', summary: 'Upload blocked', detail: this.uploadError });
      return;
    }

    const requestedFiles = event.files as File[];
    const files = requestedFiles.filter(file => this.isSupportedFile(file));
    if (files.length === 0) {
      this.uploadOngoing = false;
      this.uploadError = this.supportedFileHint;
      this.messageService.add({ severity: 'warn', summary: 'Unsupported file', detail: this.uploadError });
      return;
    }
    if (requestedFiles.length !== files.length) {
      this.uploadError = `Some files were skipped. ${this.supportedFileHint}`;
      this.messageService.add({ severity: 'warn', summary: 'Some files skipped', detail: this.uploadError });
    }
    const pendingFiles = [...new Set(files)].filter(file => this.ensureUploadState(file).status !== 'Saved');
    if (!pendingFiles.length) return;
    this.uploadOngoing = true;
    let failed = false;
    let nextIndex = 0;
    let finalAttachment: ShipmentDeliveryAttachment | undefined;
    const uploadFile = (file: File): Promise<ShipmentDeliveryAttachment | undefined> => new Promise(resolve => {
      const state = this.ensureUploadState(file);
      if (state.attachment) {
        state.status = 'Finalizing';
        resolve(state.attachment);
        return;
      }
      state.progress = 0;
      state.status = 'Uploading';
      state.completed = false;
      this.googleCloudStorageService.uploadShipmentFile(
        file,
        file.name,
        this.selectedFileType,
        null,
        this.uploadProgress,
        uploadContext.shipmentId,
        uploadContext.deliveryId,
        this.stopId,
        (jobAttachment) => {
          state.progress = 100;
          state.attachment = jobAttachment;
          state.status = 'Finalizing';
          resolve(jobAttachment);
        },
        (progress) => {
          this.updateFileProgress(progress, state);
          if (progress.state === 'error') {
            failed = true;
            resolve(undefined);
          }
        }
      );
    });
    const recordFile = async (file: File, attachment: ShipmentDeliveryAttachment, batchComplete: boolean): Promise<boolean> => {
      const state = this.ensureUploadState(file);
      try {
        if (await this.shipmentFileDownloadUrlCallback(attachment, batchComplete) === false) throw new Error('Final update failed');
        state.status = 'Saved';
        state.completed = true;
        return true;
      } catch {
        state.status = 'Final update failed';
        state.completed = true;
        this.uploadError = `File ${file.name} was uploaded, but its final update failed. Retry to finish this file.`;
        return false;
      }
    };
    const worker = async () => {
      while (nextIndex < pendingFiles.length) {
        const index = nextIndex++;
        let attachment: ShipmentDeliveryAttachment | undefined;
        try {
          attachment = await uploadFile(pendingFiles[index]);
        } catch {
          failed = true;
          this.ensureUploadState(pendingFiles[index]).status = 'Failed';
          this.uploadError = `Upload could not start for ${pendingFiles[index].name}. Retry this file.`;
          continue;
        }
        if (!attachment) continue;
        if (index === pendingFiles.length - 1) {
          finalAttachment = attachment;
          continue;
        }
        if (!await recordFile(pendingFiles[index], attachment, false)) failed = true;
      }
    };
    void Promise.all(Array.from({ length: Math.min(3, pendingFiles.length) }, () => worker()))
      .then(async () => {
        if (finalAttachment) await recordFile(pendingFiles[pendingFiles.length - 1], finalAttachment, !failed);
      })
      .catch(() => this.uploadError = 'Files were uploaded, but the final attachment update failed.')
      .finally(() => this.uploadOngoing = false);
  }

  onSelect(event: FileSelectEvent) {
    const requestedFiles = (((event as any).currentFiles ?? event.files ?? []) as File[]);
    const selectedFiles = requestedFiles.filter((file: File) => this.isSupportedFile(file));
    const previousFiles = this.uploadedFiles;
    previousFiles.filter(state => !selectedFiles.includes(state.file)).forEach(state => {
      if (state.previewUrl) URL.revokeObjectURL(state.previewUrl);
    });
    this.uploadedFiles = selectedFiles.map((file: File) => {
      const existing = previousFiles.find(state => state.file === file);
      if (existing) return existing;
      const previewUrl = URL.createObjectURL(file);
      return {
      file,
      name: file.name,
      size: file.size,
      kind: this.fileKind(file),
      iconClass: this.fileIcon(file),
      previewUrl,
      previewSafeUrl: this.sanitizer.bypassSecurityTrustResourceUrl(previewUrl),
      previewOpened: false,
      progress: 0,
      status: 'Ready',
      completed: false
    };
    });
    this.filesSelected = this.uploadedFiles.length >= 1;
    this.uploadProgress = 0;
    if (!this.filesSelected) {
      this.uploadError = this.supportedFileHint;
      this.messageService.add({ severity: 'warn', summary: 'Unsupported file', detail: this.uploadError });
    } else if (requestedFiles.length !== selectedFiles.length) {
      this.uploadError = `Some files were skipped. ${this.supportedFileHint}`;
      this.messageService.add({ severity: 'warn', summary: 'Some files skipped', detail: this.uploadError });
    } else {
      this.uploadError = '';
    }
  }

  onProgress(event: FileProgressEvent) {
    event.progress = this.uploadProgress;
  }

  clearSelection() {
    if (this.uploadOngoing) return;
    this.revokePreviewUrls();
    this.filesSelected = false;
    this.uploadedFiles = [];
    this.uploadProgress = 0;
    this.uploadOngoing = false;
    this.uploadError = '';
  }

  private updateFileProgress(progress: ShipmentUploadProgress, state: UploadFileState) {
    state.progress = progress.progress;
    state.status = progress.state === 'running'
      ? 'Uploading'
      : progress.state === 'saving'
        ? 'Saving attachment'
        : progress.state;
    state.bytesTransferred = progress.bytesTransferred;
    state.totalBytes = progress.totalBytes;
    if (progress.state === 'error') {
      state.status = 'Failed';
      state.completed = true;
      this.uploadError = progress.errorCode === 'storage/retry-limit-exceeded'
        ? `Upload timed out for ${progress.fileName}. Retry on a stable connection.`
        : `Upload failed for ${progress.fileName}${progress.errorCode ? ` (${progress.errorCode})` : ''}. Retry this file.`;
    }
    this.uploadProgress = Math.round(
      this.uploadedFiles.reduce((sum, file) => sum + file.progress, 0) / Math.max(this.uploadedFiles.length, 1)
    );
  }

  private ensureUploadState(file: File): UploadFileState {
    let state = this.uploadedFiles.find(uploadFile => uploadFile.file === file);
    if (!state) {
      const previewUrl = URL.createObjectURL(file);
      state = {
        file,
        name: file.name,
        size: file.size,
        kind: this.fileKind(file),
        iconClass: this.fileIcon(file),
        previewUrl,
        previewSafeUrl: this.sanitizer.bypassSecurityTrustResourceUrl(previewUrl),
        previewOpened: false,
        progress: 0,
        status: 'Ready',
        completed: false
      };
      this.uploadedFiles.push(state);
    }
    return state;
  }

  private revokePreviewUrls() {
    this.uploadedFiles.forEach(file => {
      if (file.previewUrl) {
        URL.revokeObjectURL(file.previewUrl);
      }
    });
  }

  private isSupportedFile(file: File): boolean {
    return this.fileKind(file) !== 'unsupported';
  }

  private fileKind(file: File): UploadFileKind {
    const mimeType = (file.type || '').toLowerCase();
    const extension = this.fileExtension(file.name);
    if (mimeType === 'application/pdf' || extension === 'pdf') return 'pdf';
    if (mimeType.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif'].includes(extension)) return 'image';
    if (mimeType.startsWith('video/') || ['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(extension)) return 'video';
    if ([
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/rtf',
      'text/rtf'
    ].includes(mimeType) || ['doc', 'docx', 'rtf'].includes(extension)) {
      return 'document';
    }
    if ([
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv'
    ].includes(mimeType) || ['xls', 'xlsx', 'csv'].includes(extension)) {
      return 'spreadsheet';
    }
    if ([
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    ].includes(mimeType) || ['ppt', 'pptx'].includes(extension)) {
      return 'presentation';
    }
    if (mimeType === 'text/plain' || extension === 'txt') return 'text';
    return 'unsupported';
  }

  private fileIcon(file: File): string {
    switch (this.fileKind(file)) {
      case 'pdf':
        return 'pi pi-file-pdf';
      case 'image':
        return 'pi pi-image';
      case 'video':
        return 'pi pi-video';
      case 'spreadsheet':
        return 'pi pi-file-excel';
      case 'presentation':
        return 'pi pi-file';
      case 'document':
      case 'text':
        return 'pi pi-file-word';
      default:
        return 'pi pi-file';
    }
  }

  private fileExtension(fileName: string): string {
    const parts = fileName.toLowerCase().split('.');
    return parts.length > 1 ? parts.pop() || '' : '';
  }

}

type UploadFileKind = 'pdf' | 'image' | 'video' | 'document' | 'spreadsheet' | 'presentation' | 'text' | 'unsupported';

interface UploadFileState {
  file: File;
  name: string;
  size: number;
  kind: UploadFileKind;
  iconClass: string;
  previewUrl?: string;
  previewSafeUrl?: SafeResourceUrl;
  attachment?: ShipmentDeliveryAttachment;
  previewOpened?: boolean;
  progress: number;
  status: string;
  completed: boolean;
  bytesTransferred?: number;
  totalBytes?: number;
}
